import React from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import './ProgressSidePanel.css';

interface ProgressSidePanelProps {
  characterData: CharacterCreationData;
  currentStep: number;
  steps: Array<{ id: string; title: string }>;
  visitedSteps: Set<number>;
  onStepClick: (stepIndex: number) => void;
}

const ProgressSidePanel: React.FC<ProgressSidePanelProps> = ({
  characterData,
  currentStep,
  steps,
  visitedSteps,
  onStepClick,
}) => {
  const isStepComplete = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0: // Ancestry
        return !!characterData.ancestry;
      case 1: // Community
        return !!characterData.community;
      case 2: // Class
        return !!characterData.class;
      case 3: // Subclass
        return !!characterData.subclass;
      case 4: // Starting Choices - optional, only complete if visited
        return visitedSteps.has(stepIndex);
      case 5: // Character Customization - optional, only complete if visited
        return visitedSteps.has(stepIndex);
      case 6: // Equipment - optional, only complete if visited
        return visitedSteps.has(stepIndex);
      case 7: // Summary
        return !!characterData.name.trim();
      default:
        return false;
    }
  };

  const isStepAccessible = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0: // Ancestry - always accessible
      case 1: // Community - always accessible  
      case 2: // Class - always accessible
        return true;
      case 3: // Subclass - requires class to be selected
      case 4: // Starting Choices - requires class to be selected
      case 5: // Character Customization - requires class to be selected
      case 6: // Equipment - requires class to be selected
        return !!characterData.class;
      case 7: // Summary - always accessible (but creation requires all required steps)
        return true;
      default:
        return false;
    }
  };

  const getStepStatusIcon = (stepIndex: number): string => {
    if (stepIndex === currentStep) return '▶';
    if (isStepComplete(stepIndex)) return '✓';
    return stepIndex < currentStep ? '✓' : '○';
  };

  const getStepTooltip = (stepIndex: number): string => {
    if (isStepAccessible(stepIndex)) return '';
    
    // Only class-dependent steps should have tooltips
    switch (stepIndex) {
      case 3: // Subclass
        return 'Select a class first to choose a subclass';
      case 4: // Starting Choices
        return 'Select a class first to see starting choices';
      case 5: // Character Customization
        return 'Select a class first to customize your character';
      case 6: // Equipment
        return 'Select a class first to choose equipment';
      default:
        return '';
    }
  };

  const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getCurrentSelections = () => {
    const selections: Array<{ label: string; value: string }> = [];

    if (characterData.ancestry) {
      selections.push({ label: 'Ancestry', value: capitalizeFirst(characterData.ancestry) });
    }

    if (characterData.community) {
      selections.push({ label: 'Community', value: capitalizeFirst(characterData.community) });
    }

    if (characterData.class) {
      selections.push({ label: 'Class', value: capitalizeFirst(characterData.class) });
    }

    if (characterData.subclass) {
      selections.push({ label: 'Subclass', value: capitalizeFirst(characterData.subclass) });
    }

    // Starting choices
    if (characterData.startingChoices) {
      const choiceCount = Object.keys(characterData.startingChoices).length;
      if (choiceCount > 0) {
        selections.push({ 
          label: 'Starting Choices', 
          value: `${choiceCount} selections made` 
        });
      }
    }

    // Appearance
    if (characterData.appearance) {
      const appearanceCount = Object.keys(characterData.appearance).filter(
        key => characterData.appearance![key as keyof typeof characterData.appearance]
      ).length;
      if (appearanceCount > 0) {
        selections.push({ 
          label: 'Appearance', 
          value: `${appearanceCount} traits selected` 
        });
      }
    }

    // Background answers
    if (characterData.backgroundAnswers && characterData.backgroundAnswers.some(item => item && item.answer && item.answer.trim())) {
      const answerCount = characterData.backgroundAnswers.filter(item => item && item.answer && item.answer.trim()).length;
      selections.push({
        label: 'Background',
        value: `${answerCount} questions answered`
      });
    }

    // Equipment
    if (characterData.selectedEquipment) {
      const equipmentCount = Object.values(characterData.selectedEquipment).filter(Boolean).length;
      if (equipmentCount > 0) {
        selections.push({ 
          label: 'Equipment', 
          value: `${equipmentCount} items selected` 
        });
      }
    }

    if (characterData.name) {
      selections.push({ label: 'Character Name', value: capitalizeFirst(characterData.name) });
    }

    return selections;
  };

  return (
    <div className="progress-side-panel">
      <div className="progress-side-panel__header">
        <h3>Character Creation Progress</h3>
      </div>

      {/* Step Navigation */}
      <div className="progress-side-panel__steps">
        <h4>Steps</h4>
        {steps.map((step, index) => (
          <button
            key={step.id}
            className={`progress-step ${
              index === currentStep ? 'progress-step--current' : ''
            } ${
              isStepComplete(index) ? 'progress-step--complete' : ''
            } ${
              !isStepAccessible(index) ? 'progress-step--disabled' : ''
            }`}
            onClick={() => isStepAccessible(index) && onStepClick(index)}
            disabled={!isStepAccessible(index)}
            title={getStepTooltip(index)}
          >
            <span className="progress-step__icon">
              {getStepStatusIcon(index)}
            </span>
            <span className="progress-step__title">
              {step.title}
            </span>
          </button>
        ))}
      </div>

      {/* Current Selections */}
      <div className="progress-side-panel__selections">
        <h4>Your Selections</h4>
        {getCurrentSelections().length > 0 ? (
          <div className="selections-list">
            {getCurrentSelections().map((selection, index) => (
              <div key={index} className="selection-item">
                <span className="selection-item__label">
                  {selection.label}:
                </span>
                <span className="selection-item__value">
                  {selection.value}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="selections-empty">
            No selections made yet. Start by choosing your ancestry.
          </p>
        )}
      </div>

      {/* Progress Summary */}
      <div className="progress-side-panel__summary">
        <div className="progress-summary">
          <span className="progress-summary__text">
            Step {currentStep + 1} of {steps.length}
          </span>
          <div className="progress-summary__bar">
            <div 
              className="progress-summary__fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressSidePanel;